import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Carousel, Image } from "antd";
import React, { useEffect } from "react";
import GlobalStoreContext from "../../../context/GlobalStoreContext";
import { Area } from "../../../models/Area";
import { Dropoff } from "../../../models/Dropoff";
import { Equipment } from "../../../models/Equipment";
import { Gate } from "../../../models/Gate";
import { HaulRoute } from "../../../models/HaulRoute";
import PlotAPI from "../../../plotAPI";

interface Props {
    currentObject: Area | Dropoff | Equipment | Gate | HaulRoute;
    // When true, don't fall back to the "No location attachments" placeholder image - render
    // nothing instead once we know there are no attachments.
    hideEmptyState?: boolean;
    onEmptyChange?: (isEmpty: boolean) => void;
}

const MapObjectAttachmentsSlider: React.FC<Props> = (props) => {
    const context = React.useContext(GlobalStoreContext);

    const [sliderItems, setSliderItems] = React.useState<React.ReactNode[]>([]);

    const getAttachments = () => {
        if (props.currentObject instanceof Area) {
            return PlotAPI.getAreaAttachments(
                context.currentProjectInfo.claimed_by_team_subdomain || "projects",
                context.currentProjectInfo.unique_token,
                props.currentObject.id
            );
        }

        if (props.currentObject instanceof Gate) {
            return PlotAPI.getGateAttachments(
                context.currentProjectInfo.claimed_by_team_subdomain || "projects",
                context.currentProjectInfo.unique_token,
                props.currentObject.id
            );
        }

        if (props.currentObject instanceof Dropoff) {
            return PlotAPI.getDropoffAttachments(
                context.currentProjectInfo.claimed_by_team_subdomain || "projects",
                context.currentProjectInfo.unique_token,
                props.currentObject.id
            );
        }

        if (props.currentObject instanceof HaulRoute) {
            return PlotAPI.getHaulRouteAttachments(
                context.currentProjectInfo.claimed_by_team_subdomain || "projects",
                context.currentProjectInfo.unique_token,
                props.currentObject.id
            );
        }

        return PlotAPI.getEquipmentAttachments(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            props.currentObject.id
        );
    };

    useEffect(() => {
        getAttachments().then((attachments) => {
            const newSliderItems: any[] = [];

            for (const attachment of attachments) {
                if (attachment.content_type.startsWith("image")) {
                    newSliderItems.push(
                        <Image
                            key={attachment.id}
                            width={"100%"}
                            height={"100%"}
                            className="rounded-lg!"
                            style={{ objectFit: "contain" }}
                            src={urlForAttachmentImage(attachment)}
                        />
                    );
                }
            }

            if (newSliderItems.length === 0 && !props.hideEmptyState) {
                newSliderItems.push(
                    <Image
                        className="rounded-lg!"
                        key={0}
                        width={"100%"}
                        height={"100%"}
                        style={{ objectFit: "contain" }}
                        src={"/img/png/No-Location-Attachment-Added.png"}
                    />
                );
            }

            props.onEmptyChange?.(newSliderItems.length === 0);

            setSliderItems(newSliderItems);
        });
    }, [props.currentObject]);

    const urlForAttachmentImage = (attachment: any) => {
        if (props.currentObject instanceof Area) {
            return (
                "/projects/" +
                context.currentProjectInfo.unique_token +
                "/areas/" +
                props.currentObject.id +
                "/areas_attachments/" +
                attachment.id +
                "/picture"
            );
        }

        if (props.currentObject instanceof Gate) {
            return (
                "/projects/" +
                context.currentProjectInfo.unique_token +
                "/gates/" +
                props.currentObject.id +
                "/gates_attachments/" +
                attachment.id +
                "/picture"
            );
        }

        if (props.currentObject instanceof Dropoff) {
            return (
                "/projects/" +
                context.currentProjectInfo.unique_token +
                "/dropoffs/" +
                props.currentObject.id +
                "/dropoffs_attachments/" +
                attachment.id +
                "/picture"
            );
        }

        if (props.currentObject instanceof HaulRoute) {
            return (
                "/projects/" +
                context.currentProjectInfo.unique_token +
                "/haul_routes/" +
                props.currentObject.id +
                "/haul_routes_attachments/" +
                attachment.id +
                "/picture"
            );
        }

        return (
            "/projects/" +
            context.currentProjectInfo.unique_token +
            "/equipment/" +
            props.currentObject.id +
            "/equipment_attachments/" +
            attachment.id +
            "/picture"
        );
    };

    if (props.hideEmptyState && sliderItems.length === 0) {
        return null;
    }

    return (
        <Carousel
            rootClassName="w-full h-full"
            className="w-full h-full"
            arrows={sliderItems.length > 1 ? true : false}
            dots={sliderItems.length > 1 ? true : false}
            prevArrow={<LeftOutlined />}
            nextArrow={<RightOutlined />}
        >
            {sliderItems.map((image) => image)}
        </Carousel>
    );
};

export default MapObjectAttachmentsSlider;
